import { faPlus, faTrash } from '@fortawesome/pro-solid-svg-icons';
import type { PermissionConditionNode } from '@supply-mover-2-0/core';
import React, { useMemo } from 'react';
import { Icon } from '../../../components/base/Icon';
import { Button } from '../../../components/forms/Button';
import { SingleSelect } from '../../../components/forms/SingleSelect';
import { useThemeColors } from '../../../providers/ThemeProvider';
import { useTranslation } from '../../../providers/TranslationProvider';
import { cn } from '../../../utils/cn';
import { getAllRbacActions } from './rbacActionList';

/**
 * Max nesting depth for permission condition groups. Three levels covers
 * realistic use cases (e.g. "A AND (B OR (C AND D))") without letting the UI
 * descend into a hard-to-read forest. The +Group button hides when we'd
 * exceed this from the current depth.
 */
const MAX_NESTING_DEPTH = 3;

export interface PermissionConditionBuilderProps {
	/** The condition tree to edit. null = unconfigured (renders an empty AND group). */
	value: PermissionConditionNode | null;
	onChange: (next: PermissionConditionNode | null) => void;
	/** Internal — current depth in the tree (root call leaves at 0). */
	_depth?: number;
}

function emptyGroup(): Extract<PermissionConditionNode, { type: 'group' }> {
	return { type: 'group', joiner: 'AND', negated: false, children: [] };
}

function emptyRule(): PermissionConditionNode {
	return { type: 'rule', action: '', negated: false };
}

/**
 * Recursive editor for PermissionConditionNode trees. The root is always
 * rendered as a group container; nested children render in their own
 * indented sub-containers. Empty groups evaluate to true at runtime (the
 * "no condition set" case), so a freshly-mounted builder with no rules is
 * functionally equivalent to `value: null`.
 */
export function PermissionConditionBuilder({
	value,
	onChange,
	_depth = 0,
}: PermissionConditionBuilderProps): React.ReactElement {
	// Always render as a group at the root. If the caller stored a bare rule,
	// promote it so the AND/OR control and NOT toggle are visible.
	const rootGroup: Extract<PermissionConditionNode, { type: 'group' }> =
		useMemo(() => {
			if (value == null) {
				return emptyGroup();
			}
			if (value.type === 'group') {
				return value;
			}
			return {
				type: 'group',
				joiner: 'AND',
				negated: false,
				children: [value],
			};
		}, [value]);

	const emit = (next: PermissionConditionNode): void => {
		// Treat an empty root group as null so saved state stays clean
		// (no point persisting a condition with no rules).
		if (
			next.type === 'group' &&
			next.children.length === 0 &&
			!next.negated &&
			next.joiner === 'AND'
		) {
			onChange(null);
			return;
		}
		onChange(next);
	};

	return (
		<GroupEditor
			group={rootGroup}
			onChange={emit}
			depth={_depth}
			canDelete={false}
		/>
	);
}

interface GroupEditorProps {
	group: Extract<PermissionConditionNode, { type: 'group' }>;
	onChange: (next: PermissionConditionNode) => void;
	/** Allow the parent to remove this group entirely (false at root). */
	canDelete: boolean;
	onDelete?: () => void;
	depth: number;
}

function GroupEditor({
	group,
	onChange,
	canDelete,
	onDelete,
	depth,
}: GroupEditorProps): React.ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const canNestGroups = depth + 1 < MAX_NESTING_DEPTH;

	const updateChild = (
		index: number,
		next: PermissionConditionNode | null,
	): void => {
		const newChildren = [...group.children];
		if (next == null) {
			newChildren.splice(index, 1);
		} else {
			newChildren[index] = next;
		}
		onChange({ ...group, children: newChildren });
	};

	const addRule = (): void => {
		onChange({ ...group, children: [...group.children, emptyRule()] });
	};

	const addGroup = (): void => {
		onChange({ ...group, children: [...group.children, emptyGroup()] });
	};

	return (
		<div
			className={cn(
				'rounded-md border p-3',
				colors.classes.borderDefault,
				depth === 0 ? colors.classes.bgPrimary : colors.classes.bgSecondary,
			)}
		>
			{/* Group header — NOT toggle + joiner control + delete */}
			<div className="flex items-center gap-2 mb-2">
				<label className="flex items-center gap-1 cursor-pointer">
					<input
						type="checkbox"
						checked={group.negated}
						onChange={(e) => {
							onChange({ ...group, negated: e.target.checked });
						}}
						className="cursor-pointer"
					/>
					<span
						className={cn(
							'text-xs font-semibold uppercase',
							colors.classes.textSecondary,
						)}
					>
						{t('NOT')}
					</span>
				</label>

				<div className="flex items-center rounded border overflow-hidden">
					{(['AND', 'OR'] as const).map((j) => (
						<button
							key={j}
							type="button"
							onClick={() => {
								onChange({ ...group, joiner: j });
							}}
							className={cn(
								'px-3 py-1 text-xs font-semibold transition-colors',
								group.joiner === j
									? cn(colors.classes.bgInfo, 'text-white')
									: cn(
											colors.classes.bgPrimary,
											colors.classes.textSecondary,
											'hover:' + colors.classes.bgSecondary,
										),
							)}
						>
							{t(j)}
						</button>
					))}
				</div>

				<span className={cn('text-xs', colors.classes.textMuted)}>
					{group.children.length === 0
						? t('(no rules — condition always passes)')
						: t(`${String(group.children.length)} rule(s)`)}
				</span>

				<div className="flex-1" />

				{canDelete && onDelete != null && (
					<button
						type="button"
						onClick={onDelete}
						className={cn(
							'p-1 rounded transition-colors',
							colors.classes.textMuted,
							'hover:' + colors.classes.textError,
						)}
						title={t('Remove group')}
					>
						<Icon icon={faTrash} size="xs" />
					</button>
				)}
			</div>

			{/* Children */}
			{group.children.length > 0 && (
				<div className="space-y-2 pl-2 border-l-2 border-dashed border-gray-300 dark:border-gray-600">
					{group.children.map((child, index) => (
						<div key={index}>
							{child.type === 'rule' ? (
								<RuleEditor
									rule={child}
									onChange={(next) => {
										updateChild(index, next);
									}}
									onDelete={() => {
										updateChild(index, null);
									}}
								/>
							) : (
								<GroupEditor
									group={child}
									depth={depth + 1}
									canDelete
									onChange={(next) => {
										updateChild(index, next);
									}}
									onDelete={() => {
										updateChild(index, null);
									}}
								/>
							)}
						</div>
					))}
				</div>
			)}

			{/* Add buttons */}
			<div className="flex gap-2 mt-2">
				<Button
					variant="outline"
					size="xs"
					onClick={addRule}
					icon={<Icon icon={faPlus} size="xs" />}
				>
					{t('Add rule')}
				</Button>
				{canNestGroups && (
					<Button
						variant="outline"
						size="xs"
						onClick={addGroup}
						icon={<Icon icon={faPlus} size="xs" />}
					>
						{t('Add group')}
					</Button>
				)}
			</div>
		</div>
	);
}

interface RuleEditorProps {
	rule: Extract<PermissionConditionNode, { type: 'rule' }>;
	onChange: (next: PermissionConditionNode) => void;
	onDelete: () => void;
}

function RuleEditor({
	rule,
	onChange,
	onDelete,
}: RuleEditorProps): React.ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();

	const actionOptions = useMemo(
		() =>
			getAllRbacActions().map((a) => ({
				value: a.action,
				label: a.pathLabel,
			})),
		[],
	);

	return (
		<div className="flex items-center gap-2">
			<label className="flex items-center gap-1 cursor-pointer shrink-0">
				<input
					type="checkbox"
					checked={rule.negated}
					onChange={(e) => {
						onChange({ ...rule, negated: e.target.checked });
					}}
					className="cursor-pointer"
				/>
				<span
					className={cn(
						'text-xs font-semibold uppercase',
						colors.classes.textSecondary,
					)}
				>
					{t('NOT')}
				</span>
			</label>

			<div className="flex-1">
				<SingleSelect
					name="action"
					size="sm"
					value={rule.action.length > 0 ? rule.action : null}
					options={actionOptions}
					placeholder={t('Select an action...')}
					onChange={(value) => {
						onChange({ ...rule, action: value });
					}}
					allowClear={false}
					searchable
				/>
			</div>

			<button
				type="button"
				onClick={onDelete}
				className={cn(
					'p-1 rounded transition-colors shrink-0',
					colors.classes.textMuted,
					'hover:' + colors.classes.textError,
				)}
				title={t('Remove rule')}
			>
				<Icon icon={faTrash} size="xs" />
			</button>
		</div>
	);
}
